import { createContext, useContext, useEffect, useState } from "react";
import {
  fetchMe,
  loginUser,
  registerUser,
  logoutUser,
  setToken,
  clearToken,
  getToken,
} from "@/lib/api";

const AuthContext = createContext(null);

const ACTIVE_KID_KEY = "km_active_kid";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = signed in
  const [activeKidId, setActiveKidId] = useState(
    () => localStorage.getItem(ACTIVE_KID_KEY) || null
  );

  useEffect(() => {
    (async () => {
      if (!getToken()) {
        setUser(false);
        return;
      }
      try {
        const me = await fetchMe();
        setUser(me);
      } catch (e) {
        // Only a real rejection clears the session — a flaky mobile connection
        // must not log the parent out for good.
        if (e.response?.status === 401) clearToken();
        setUser(false);
      }
    })();
  }, []);

  const selectKid = (kidId) => {
    if (kidId) localStorage.setItem(ACTIVE_KID_KEY, kidId);
    else localStorage.removeItem(ACTIVE_KID_KEY);
    setActiveKidId(kidId);
  };

  const login = async (email, password) => {
    const data = await loginUser({ email, password });
    setToken(data.token);
    setUser(data.user);
    selectKid(null);
    return data.user;
  };

  const signup = async (email, password, name) => {
    const data = await registerUser({ email, password, name });
    sessionStorage.setItem("km_first_run", "1");
    setToken(data.token);
    setUser(data.user);
    selectKid(null);
    return data.user;
  };

  const logout = async () => {
    await logoutUser();
    clearToken();
    selectKid(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider
      value={{ user, activeKidId, selectKid, login, signup, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
